import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { StatusCodes } from 'http-status-codes';
import { EMPTY } from 'rxjs';
import {
  catchError,
  exhaustMap,
  filter,
  finalize,
  map,
  take,
  tap,
} from 'rxjs/operators';
import { isFunction, WfApiErrorResponse } from 'wflow-cdk';
import { OrganizationAPIService } from 'wflow-main-app/src/app/core/api/api/organization';
import { APP_ROUTES } from '../../routing.const';
import * as IdentitiesActions from './identity.actions';

@Injectable()
export class IdentitiesEffects {
  readonly #loadedIdentity = new Set<string>();

  readonly loadIdentity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IdentitiesActions.LoadIdentity),
      filter(
        (payload: { orgId: string }) =>
          !this.#loadedIdentity.has(payload.orgId),
      ),
      tap(({ orgId }) => {
        this.#loadedIdentity.add(orgId);
      }),
      exhaustMap(({ orgId }) =>
        this.organizationService.getOrganizationIdentity(orgId).pipe(
          take(1),
          map((data) =>
            IdentitiesActions.SetIdentity({
              id: orgId,
              data,
            }),
          ),
          catchError((err: WfApiErrorResponse) => {
            if (
              err?.status === StatusCodes.FORBIDDEN ||
              err?.status === StatusCodes.UNAUTHORIZED ||
              err.status === 0 ||
              isFunction(err)
            ) {
              return EMPTY;
            }

            if (err?.status === StatusCodes.NOT_FOUND) {
              this.router.navigate([APP_ROUTES.NOTFOUND]);
              throw new Error(
                this.translate.instant('ADMIN.ORGANIZATION.ERRORS.OrgNotFind'),
              );
            }

            if (err?.status === StatusCodes.PAYMENT_REQUIRED) {
              this.router.navigate([APP_ROUTES.EXPIRED, orgId]);
              return EMPTY;
            }

            throw new Error(
              this.translate.instant('ADMIN.ORGANIZATION.ERRORS.OrgNotWork'),
            );
          }),
          finalize(() => this.#loadedIdentity.delete(orgId)),
        ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private organizationService: OrganizationAPIService,
    private translate: TranslateService,
    private router: Router,
  ) {}
}
